import { parse } from "acorn";
import { generate } from "astring";

import { type ProgramNode, rewriteProgram } from "./normalizeAst.ts";

const FUNCTION_EXPRESSION_PATTERN = /^(?:async\s+)?function\s*\(/;
const EXPORT_DEFAULT_PATTERN = /^export\s+default\s+/;

export const CODE_MUST_EXPORT_FUNCTION_ERROR = "Code must export a function";

const parseOptions = {
	ecmaVersion: "latest" as const,
	sourceType: "module" as const,
	allowReturnOutsideFunction: true,
	allowAwaitOutsideFunction: true,
};

type Node = {
	type: string;
	[key: string]: unknown;
};

const FUNCTION_NODE_TYPES = new Set([
	"FunctionDeclaration",
	"FunctionExpression",
	"ArrowFunctionExpression",
]);

const ensureTerminated = (value: string): string => {
	const trimmed = value.trim();
	return trimmed.endsWith(";") ? trimmed : `${trimmed};`;
};

const generateStatement = (node: Node): string =>
	generate(node, { comments: false });

const isNode = (value: unknown): value is Node =>
	typeof value === "object" &&
	value !== null &&
	"type" in (value as Record<string, unknown>);

const ensureFunctionNode = (node: unknown) => {
	if (!isNode(node) || !FUNCTION_NODE_TYPES.has(node.type)) {
		throw new Error(CODE_MUST_EXPORT_FUNCTION_ERROR);
	}
};

const buildExportFromReturn = (argument: unknown): string => {
	if (!isNode(argument)) {
		throw new Error(CODE_MUST_EXPORT_FUNCTION_ERROR);
	}

	if (
		argument.type === "FunctionExpression" ||
		argument.type === "ArrowFunctionExpression"
	) {
		const functionSource = generate(argument, { comments: false });
		return ensureTerminated(`export default ${functionSource}`);
	}

	if (argument.type === "Identifier") {
		const potentialName = (argument as Record<string, unknown>)["name"];
		if (typeof potentialName === "string") {
			return ensureTerminated(`export default ${potentialName}`);
		}
	}

	throw new Error(CODE_MUST_EXPORT_FUNCTION_ERROR);
};

const createModuleFromProgram = (program: ProgramNode): string => {
	const statements = Array.isArray(program.body) ? program.body : [];

	if (statements.length === 0) {
		throw new Error(CODE_MUST_EXPORT_FUNCTION_ERROR);
	}

	const lastStatement = statements[statements.length - 1];

	if (lastStatement?.type !== "ReturnStatement") {
		throw new Error(CODE_MUST_EXPORT_FUNCTION_ERROR);
	}

	const prelude = statements
		.slice(0, -1)
		.map((node) => generateStatement(node).trim())
		.filter(Boolean);

	const exportStatement = buildExportFromReturn(
		isNode(lastStatement)
			? (lastStatement as { argument?: unknown }).argument
			: undefined,
	);

	return [...prelude, exportStatement].filter(Boolean).join("\n\n");
};

const assertDefaultExportIsFunction = (source: string) => {
	try {
		const program = parse(source, parseOptions) as unknown as ProgramNode;
		const exportDeclaration = program.body.find(
			(node) => node?.type === "ExportDefaultDeclaration",
		);
		const declaration =
			exportDeclaration && isNode(exportDeclaration)
				? (exportDeclaration as Node)["declaration"]
				: undefined;
		ensureFunctionNode(declaration);
	} catch (error) {
		if (
			error instanceof Error &&
			error.message === CODE_MUST_EXPORT_FUNCTION_ERROR
		) {
			throw error;
		}
		throw new Error(CODE_MUST_EXPORT_FUNCTION_ERROR);
	}
};

export const normalizeUserCode = (source: string): string => {
	const trimmed = (source ?? "").trim();

	if (!trimmed) {
		throw new Error(CODE_MUST_EXPORT_FUNCTION_ERROR);
	}

	if (EXPORT_DEFAULT_PATTERN.test(trimmed)) {
		assertDefaultExportIsFunction(trimmed);
		return ensureTerminated(trimmed);
	}

	if (FUNCTION_EXPRESSION_PATTERN.test(trimmed)) {
		return ensureTerminated(`export default ${trimmed}`);
	}

	try {
		const program = parse(trimmed, parseOptions) as unknown as ProgramNode;
		const normalizedProgram = rewriteProgram(program);
		return createModuleFromProgram(normalizedProgram).trim();
	} catch (error) {
		if (error instanceof Error) {
			throw error;
		}
		throw new Error(String(error));
	}
};
